import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import PatientModal from 'components/Modals/PatientModal.js';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import Header from 'components/Headers/Header';
import AudioRecorder from '../api/Recorder';
import { GlobalContext } from '../api/GlobalContext';
import { isNativeRecorder } from '../api/nativeRecorder';
import { liveStore } from 'agent/liveStore';
import { setAttributionNames } from 'agent/logAttribution';
import SegmentedRecorder from './multirecord/SegmentedRecorder';
import UnplacedReview from './multirecord/UnplacedReview';
import {
  IGNORE,
  addChunk,
  applyReview,
  bestGuessOf,
  choiceOf,
  chooseVisit,
  clearPlaced,
  movePlaced,
  needsChoice,
  readPlaced,
  readUnplaced,
  recordAttributionHints,
  removePlaced,
  retryPlacement,
  subscribeReview,
  suggestedOwner,
} from './multirecord/reviewSession';

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString + 'Z');
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateDisplay(date) {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateRangeCompact(start, end) {
  if (!start || !end) return 'Date Range';
  const s = new Date(start);
  const e = new Date(end);
  const fmt = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
  return `${fmt(s)} – ${fmt(e)}`;
}

const MultiRecorder = () => {
  const { apiClient } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [recentConversations, setRecentConversations] = useState([]);
  const [selectedConversationIds, setSelectedConversationIds] = useState([]);
  const selectedConversationIdsRef = useRef([]);
  useEffect(() => {
    selectedConversationIdsRef.current = selectedConversationIds;
  }, [selectedConversationIds]);

  const [recorderStatus, setRecorderStatus] = useState(
    'Select your visits to start recording.',
  );

  const handleRecorderStatus = (status) => {
    setRecorderStatus(status);
    // Mirror recording state into the Comprehend Live rail (no-op when the
    // panel is feature-flagged off — the store just holds state).
    if (typeof status === 'string') {
      if (status.startsWith('Recording')) {
        liveStore.setRecordingSessions(selectedConversationIdsRef.current);
      } else if (
        status.startsWith('Transcription') ||
        status.startsWith('Uploading') ||
        status.startsWith('Select your visits')
      ) {
        liveStore.setRecordingSessions([]);
      }
    }
    // startsWith — for dev users, the BE appends ` · via <model>` to the
    // state string (see _state_with_dev_suffix in Conversation.py). Bare
    // values still match themselves so non-dev users are unaffected.
    if (
      typeof status === 'string' &&
      status.startsWith('Transcription Complete!')
    ) {
      setRecentConversations((prev) =>
        prev.map((c) =>
          selectedConversationIdsRef.current.includes(c.conversationId)
            ? { ...c, status: 'Transcription Complete!' }
            : c,
        ),
      );
    }
  };
  // Wait for the profile, then keep one recorder implementation per mount.
  const [contextEnabled, setContextEnabled] = useState(() =>
    apiClient.name == null && apiClient.onProfileUpdated
      ? null
      : apiClient.features?.multirecord_context_and_error_prevention === true,
  );
  useEffect(() => {
    if (contextEnabled !== null) return undefined;
    const selectRecorder = () => {
      if (apiClient.name == null) return;
      setContextEnabled((previous) => previous === null
        ? apiClient.features?.multirecord_context_and_error_prevention === true
        : previous);
    };
    apiClient.onProfileUpdated(selectRecorder);
    selectRecorder();
    return () => apiClient.offProfileUpdated(selectRecorder);
  }, [apiClient, contextEnabled]);
  const useWebSegmenter = contextEnabled === true && !isNativeRecorder();
  const errorPrevention = contextEnabled;
  const sessionEmail = apiClient.sessionEmail;
  const [isActive, setIsActive] = useState(false);
  const [review, setReview] = useState(() => ({
    unplaced: readUnplaced(sessionEmail),
    placed: readPlaced(sessionEmail),
  }));
  const [isPlacing, setIsPlacing] = useState(false);
  // The clinician opened the review themselves (nothing was unplaced).
  const [reviewRequested, setReviewRequested] = useState(false);
  useEffect(() => subscribeReview(sessionEmail, setReview), [sessionEmail]);
  // Writes stay in the store, not in this component: the last chunk's upload
  // can land after the page has unmounted. Names are saved with each line
  // because the visit list may not be loaded when the review reopens.
  const visitNamesRef = useRef({});
  const handleAttributed = useCallback(
    (data, targets, spokenAt) =>
      addChunk(sessionEmail, data, targets, spokenAt, visitNamesRef.current),
    [sessionEmail],
  );

  const [isLoading, setIsLoading] = useState(false);
  const [detailsById, setDetailsById] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [tooltipData, setTooltipData] = useState(null);
  const hoverTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, conversation }
  const [editDialog, setEditDialog] = useState(null); // { type: 'visit', conversation, value }
  const [editPatientConversation, setEditPatientConversation] = useState(null); // conversation whose patient is being edited

  // Date range picker
  const [startDate, setStartDate] = useState(() => {
    const stored = sessionStorage.getItem('startDate');
    return stored ? new Date(stored) : null;
  });
  const [endDate, setEndDate] = useState(() => {
    const stored = sessionStorage.getItem('endDate');
    return stored ? new Date(stored) : null;
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateInputValue, setDateInputValue] = useState('');
  const datePickerRef = useRef(null);

  const loadRecentConversations = useCallback(() => {
    setIsLoading(true);
    return apiClient
      .updateConversations()
      .then(() => {
        const now = new Date();
        const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000);
        const endOfToday = new Date(now);
        endOfToday.setHours(23, 59, 59, 999);
        const horizon =
          endOfToday > twoHoursFromNow ? endOfToday : twoHoursFromNow;

        // No count cap: the list is bounded by the date range alone.
        const list = (apiClient.conversations || []).filter((c) => {
          const convDate = new Date(c.createdAt + 'Z');
          // Don't show visits more than 2 hours into the future
          if (convDate > horizon) return false;
          // Apply date range filter
          if (startDate && convDate < startDate) return false;
          if (endDate) {
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            if (convDate > endOfDay) return false;
          }
          return true;
        });

        setRecentConversations(list);
        visitNamesRef.current = Object.fromEntries(
          list.map((c) => [
            c.conversationId,
            c.patientName || c.conversationName || '',
          ]),
        );
        setAttributionNames(visitNamesRef.current);
        // Keep only selections that are still present
        setSelectedConversationIds((prev) =>
          prev.filter((id) => list.some((c) => c.conversationId === id)),
        );
      })
      .catch((error) => {
        console.error('Failed to load conversations', error);
        toast.error('Could not load recent conversations.');
      })
      .finally(() => setIsLoading(false));
  }, [apiClient, startDate, endDate]);

  useEffect(() => {
    loadRecentConversations();
  }, [loadRecentConversations]);

  const toggleSelection = (conversationId) => {
    setSelectedConversationIds((prev) =>
      prev.includes(conversationId)
        ? prev.filter((id) => id !== conversationId)
        : [...prev, conversationId],
    );
  };

  const fetchDetails = useCallback(
    (conversationId) => {
      if (detailsById[conversationId] || loadingDetails[conversationId]) {
        return Promise.resolve(detailsById[conversationId]);
      }
      setLoadingDetails((prev) => ({ ...prev, [conversationId]: true }));
      return apiClient
        .getConversationDetails(conversationId)
        .then((data) => {
          if (data && !data.error) {
            setDetailsById((prev) => ({ ...prev, [conversationId]: data }));
            return data;
          }
          return null;
        })
        .catch((error) => {
          console.error('Failed to load conversation details', error);
          return null;
        })
        .finally(() => {
          setLoadingDetails((prev) => ({ ...prev, [conversationId]: false }));
        });
    },
    [apiClient, detailsById, loadingDetails],
  );

  const getTranscriptSnippet = (conversationId) => {
    const transcript = detailsById[conversationId]?.transcript;
    if (!transcript) return null;
    return transcript;
  };

  const clearTooltip = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setTooltipData(null);
  };

  const handleCardLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    hideTimerRef.current = setTimeout(() => {
      setTooltipData(null);
    }, 120);
  };

  const scheduleTooltip = (conversationId, event) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    const rect = event.currentTarget.getBoundingClientRect();
    // Show transcript preview centered on the page (better for small screens).
    const width = Math.min(Math.max(rect.width + 120, 280), 420);
    const maxWidth = Math.max(260, window.innerWidth - 24);
    const position = { width: Math.min(width, maxWidth), anchor: 'center' };
    hoverTimerRef.current = setTimeout(() => {
      fetchDetails(conversationId).then((data) => {
        const transcript =
          getTranscriptSnippet(conversationId) || data?.transcript;
        setTooltipData({
          id: conversationId,
          content: transcript || 'No transcript available yet.',
          ...position,
        });
      });
    }, 2000);
  };

  useEffect(() => {
    if (tooltipData && tooltipData.id) {
      const updated = getTranscriptSnippet(tooltipData.id);
      if (updated && updated !== tooltipData.content) {
        setTooltipData((prev) =>
          prev && prev.id === tooltipData.id
            ? { ...prev, content: updated }
            : prev,
        );
      }
    }
  }, [detailsById, tooltipData]);

  const selectedNames = useMemo(() => {
    return selectedConversationIds
      .map((id) => recentConversations.find((c) => c.conversationId === id))
      .filter(Boolean)
      .map((convo) => convo.conversationName || 'Untitled Visit');
  }, [selectedConversationIds, recentConversations]);

  // After each chunk upload, refresh details for the convos we're recording into (like single recorder
  // refreshing the visit). Update transcript preview and surface any transcription errors.
  const refreshRecordingConvos = useCallback(() => {
    const ids = selectedConversationIdsRef.current;
    if (ids.length === 0) return;
    const results = ids.map((id) =>
      apiClient.getConversationDetails(id).then((data) => {
        if (data && !data.error) {
          setDetailsById((prev) => ({ ...prev, [id]: data }));
          if (
            data.status &&
            typeof data.status === 'string' &&
            data.status.includes('TRANSCRIPTION_ERROR')
          ) {
            toast.error(
              `Transcription error for one of the visits. Check visit details.`,
            );
          }
        } else if (data && data.error) {
          toast.error(data.error);
        }
        return data;
      }),
    );
    Promise.all(results).then((allData) => {
      const allComplete = allData.every(
        (data) =>
          data &&
          !data.error &&
          typeof data.status === 'string' &&
          data.status.startsWith('Transcription Complete!'),
      );
      if (allComplete) {
        setRecorderStatus('Transcription Complete!');
        setRecentConversations((prev) =>
          prev.map((c) =>
            ids.includes(c.conversationId)
              ? { ...c, status: 'Transcription Complete!' }
              : c,
          ),
        );
      }
    });
  }, [apiClient]);

  // Lines routed (or dismissed) earlier whose write did not finish.
  // Busy while it runs, so Done cannot place the same lines a second time.
  useEffect(() => {
    setIsPlacing(true);
    retryPlacement(apiClient, sessionEmail).finally(() => setIsPlacing(false));
  }, [apiClient, sessionEmail]);

  const finishReview = (dismissed) => {
    // Clinical lines nobody picked a patient for, which go to a best guess.
    const guessed = pendingLines.filter((line) => choiceOf(line) == null &&
      (dismissed ? bestGuessOf(line) : suggestedOwner(line)) != null).length;
    setIsPlacing(true);
    applyReview(apiClient, sessionEmail, { dismissed })
      .then((failed) => {
        if (failed.length) {
          toast.error(
            'Some changes could not be saved to their visit yet. We will retry next time you open the Multi Recorder.',
          );
        } else if (guessed > 0) {
          toast.success(`Saved. ${guessed} ${guessed === 1 ? 'line uses' : 'lines use'} Comprehend’s best guess.`);
        } else if (pendingLines.some((line) => choiceOf(line) !== IGNORE)) {
          toast.success('Saved to the visits.');
        }
        setReviewRequested(false);
        refreshRecordingConvos();
      })
      .catch((error) => toast.error(error.message))
      .finally(() => setIsPlacing(false));
  };

  const describeVisit = (conversationId, line) => {
    const conversation = recentConversations.find(
      (c) => c.conversationId === conversationId,
    );
    return (
      conversation?.patientName ||
      conversation?.conversationName ||
      line?.names?.[conversationId] ||
      `Visit ${conversationId}`
    );
  };

  // Shown whenever lines are waiting and nothing is recording or uploading
  // (placing mid-upload would race the chunk's own transcript write) — on a
  // later visit to this page, and even if the flag has since been turned off,
  // because these lines are on no visit until they are routed.
  const pendingLines = review.unplaced.filter(needsChoice);
  const showReview =
    !isActive && (pendingLines.length > 0 || (reviewRequested && review.placed.length > 0));

  // A new recording starts a new review. Unplaced lines are never cleared here
  // (the review blocks recording until they are routed), only the context.
  useEffect(() => {
    if (isActive && !pendingLines.length) clearPlaced(sessionEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Close date picker on outside click
  useEffect(() => {
    if (!showDatePicker) return;
    const handleOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showDatePicker]);

  const saveDateRange = (start, end) => {
    if (start) {
      const s = new Date(start);
      s.setUTCHours(0, 0, 0, 0);
      sessionStorage.setItem('startDate', s);
    }
    if (end) {
      const e = new Date(end);
      e.setUTCHours(23, 59, 59, 999);
      sessionStorage.setItem('endDate', e);
    }
  };

  const clearDateRange = () => {
    setStartDate(null);
    setEndDate(null);
    setDateInputValue('');
    sessionStorage.removeItem('startDate');
    sessionStorage.removeItem('endDate');
  };

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [contextMenu]);

  const handleCardContextMenu = (e, conversation) => {
    e.preventDefault();
    e.stopPropagation();
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    const menuWidth = 180;
    const menuHeight = 120;
    const x =
      e.clientX + menuWidth > window.innerWidth
        ? e.clientX - menuWidth
        : e.clientX;
    const y =
      e.clientY + menuHeight > window.innerHeight
        ? e.clientY - menuHeight
        : e.clientY;
    setContextMenu({ x, y, conversation });
  };

  const handleSaveEdit = () => {
    if (!editDialog) return;
    const { conversation, value } = editDialog;
    setEditDialog(null);
    // Don't save if unchanged or blank
    if (!value.trim() || value === conversation.conversationName) return;
    apiClient
      .editConversation(conversation.conversationId, 'name', value)
      .then(() => {
        setRecentConversations((prev) =>
          prev.map((c) =>
            c.conversationId === conversation.conversationId
              ? { ...c, conversationName: value }
              : c,
          ),
        );
        toast.success('Visit renamed.');
      })
      .catch(() => toast.error('Failed to rename visit.'));
  };

  const handleSavePatient = async (fName, lName, dob) => {
    if (!editPatientConversation) return;
    const patientUid = editPatientConversation.group;
    const patient = (apiClient.patients || []).find(
      (p) => p.uid === patientUid,
    );
    const fullName = lName ? `${fName} ${lName}` : fName;
    try {
      if (!patient || patient.firstName !== fName)
        await apiClient.editPatient(patientUid, 'firstName', fName);
      if (!patient || patient.lastName !== lName)
        await apiClient.editPatient(patientUid, 'lastName', lName);
      if (!patient || patient.dob !== dob)
        await apiClient.editPatient(patientUid, 'dob', dob);
      setRecentConversations((prev) =>
        prev.map((c) =>
          c.group === patientUid ? { ...c, patientName: fullName } : c,
        ),
      );
      toast.success('Patient updated.');
    } catch {
      toast.error('Failed to update patient.');
    }
    setEditPatientConversation(null);
  };

  const selectionCountBadge = (
    <Badge
      color={selectedConversationIds.length ? 'primary' : 'secondary'}
      pill
    >
      {selectedConversationIds.length} Selected
    </Badge>
  );

  return (
    <>
      <Header isLite={true} />
      <Container className="mt--7 mt-md--9" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader>
                <div
                  className="d-flex align-items-center flex-wrap"
                  style={{ gap: '8px' }}
                >
                  <h3 className="mb-0 mr-auto">Multi Recorder</h3>
                  <div
                    className="d-flex align-items-center flex-wrap"
                    style={{ gap: '6px' }}
                  >
                    {/* Date range picker */}
                    <div style={{ position: 'relative', zIndex: 100 }}>
                      <button
                        type="button"
                        onClick={() => setShowDatePicker((v) => !v)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '5px 10px',
                          borderRadius: '20px',
                          border: '1.5px solid',
                          background: startDate || endDate ? '#f0f4ff' : '#fff',
                          color: '#525f7f',
                          fontSize: '0.78rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          boxShadow: showDatePicker
                            ? '0 0 0 2px rgba(94,114,228,0.25)'
                            : 'none',
                          borderColor: showDatePicker
                            ? '#5e72e4'
                            : startDate || endDate
                              ? '#5e72e4'
                              : '#dee2e6',
                        }}
                      >
                        <i
                          className="fa fa-calendar"
                          style={{
                            color: '#5e72e4',
                            fontSize: '0.75rem',
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {startDate && endDate
                            ? formatDateRangeCompact(startDate, endDate)
                            : 'Date Range'}
                        </span>
                        {startDate || endDate ? (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              clearDateRange();
                            }}
                            style={{
                              marginLeft: '2px',
                              color: '#8898aa',
                              fontWeight: 700,
                              lineHeight: 1,
                              flexShrink: 0,
                            }}
                          >
                            ×
                          </span>
                        ) : (
                          <i
                            className="fa fa-chevron-down"
                            style={{
                              fontSize: '0.6rem',
                              color: '#8898aa',
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </button>
                    </div>
                    <span
                      className="d-none d-md-inline text-muted mr-1"
                      style={{ fontSize: '0.82rem', fontWeight: 500 }}
                    >
                      Selections
                    </span>
                    <span className="mr-2">{selectionCountBadge}</span>
                    <Button
                      color="success"
                      size="sm"
                      onClick={loadRecentConversations}
                      disabled={isLoading}
                    >
                      {isLoading ? <Spinner size="sm" /> : 'Refresh'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                {/* Status bar */}
                <div
                  className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 p-3 rounded"
                  style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}
                >
                  <div className="mb-2 mb-md-0">
                    <div
                      className="text-uppercase text-muted"
                      style={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                      }}
                    >
                      Status
                    </div>
                    <div className="font-weight-bold mt-1">
                      {recorderStatus}
                    </div>
                  </div>
                  <div
                    className="text-muted ml-md-4"
                    style={{
                      fontSize: '0.8rem',
                      lineHeight: '1.5',
                      maxWidth: '600px',
                    }}
                  >
                    {
                      'Select your visits, hit record, and complete them as usual\u2014we\u2019ll automatically split the recording into the correct visit for each patient. Hover over a visit card to preview the transcript. '
                    }
                    <a
                      href="https://www.notion.so/32068e7a57a98079bfbcc762ee44eee3?pvs=25"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Learn how it works &rarr;
                    </a>
                  </div>
                </div>

                {/* Record button */}
                <div className="d-flex justify-content-center mb-4">
                  {contextEnabled === null ? <Spinner /> : useWebSegmenter ? (
                    <SegmentedRecorder
                      recordButtonId="multi-recorder-button"
                      conversationIds={selectedConversationIds}
                      isDisabled={selectedConversationIds.length === 0}
                      refresh={refreshRecordingConvos}
                      setStatus={handleRecorderStatus}
                      onAttributed={errorPrevention ? handleAttributed : undefined}
                      onActiveChange={setIsActive}
                    />
                  ) : (
                    <AudioRecorder
                      recordButtonId="multi-recorder-button"
                      conversationIds={selectedConversationIds}
                      isDisabled={selectedConversationIds.length === 0}
                      refresh={refreshRecordingConvos}
                      setStatus={handleRecorderStatus}
                      seconds_transcribed={0}
                      multirecordContext={contextEnabled}
                    />
                  )}
                </div>
                {errorPrevention && !isActive && !showReview && review.placed.length > 0 && (
                  <div className="text-center mb-3" style={{ marginTop: -12 }}>
                    <button
                      type="button"
                      onClick={() => setReviewRequested(true)}
                      style={{
                        border: 0,
                        background: 'transparent',
                        color: '#2b6cb0',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Review what we heard
                    </button>
                  </div>
                )}
                {isLoading ? (
                  <div className="text-center my-4">
                    <Spinner />
                  </div>
                ) : recentConversations.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No recent visits available. Try refreshing.
                  </div>
                ) : (
                  <Row>
                    {recentConversations.map((conversation) => (
                      <Col
                        key={conversation.conversationId}
                        xs="12"
                        sm="6"
                        md="4"
                        lg="3"
                        className="mb-3"
                      >
                        <div
                          className={`h-100 rounded p-2 ${
                            selectedConversationIds.includes(
                              conversation.conversationId,
                            )
                              ? 'bg-success text-white border-0 shadow-sm'
                              : 'border border-200'
                          }`}
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            toggleSelection(conversation.conversationId)
                          }
                          onContextMenu={(e) =>
                            handleCardContextMenu(e, conversation)
                          }
                          onMouseEnter={(e) =>
                            scheduleTooltip(conversation.conversationId, e)
                          }
                          onMouseLeave={handleCardLeave}
                          onFocus={(e) =>
                            scheduleTooltip(conversation.conversationId, e)
                          }
                          onBlur={handleCardLeave}
                        >
                          <div
                            className="mt-1"
                            style={{
                              position: 'relative',
                              paddingRight: '20px',
                            }}
                          >
                            <button
                              title="Options"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCardContextMenu(e, conversation);
                              }}
                              style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                padding: 0,
                                background: 'transparent',
                                color: selectedConversationIds.includes(
                                  conversation.conversationId,
                                )
                                  ? 'rgba(255,255,255,0.7)'
                                  : '#8898aa',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                  selectedConversationIds.includes(
                                    conversation.conversationId,
                                  )
                                    ? 'rgba(255,255,255,0.15)'
                                    : '#e9ecef';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  'transparent';
                              }}
                            >
                              <i
                                className="fa fa-ellipsis-v"
                                style={{
                                  fontSize: '0.8rem',
                                  pointerEvents: 'none',
                                }}
                              />
                            </button>
                            <div
                              className="font-weight-bold"
                              style={{
                                fontSize: '0.9rem',
                                lineHeight: '1.2rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              {(() => {
                                const isSelected =
                                  selectedConversationIds.includes(
                                    conversation.conversationId,
                                  );
                                const dotColor =
                                  conversation.secondsTranscribed === 0
                                    ? 'rgb(242, 105, 105)'
                                    : conversation.secondsTranscribed > 0 &&
                                        conversation.status ===
                                          'Transcription Complete!'
                                      ? 'rgb(255, 208, 0)'
                                      : null;
                                return dotColor && !isSelected ? (
                                  <span
                                    style={{
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      backgroundColor: dotColor,
                                      flexShrink: 0,
                                    }}
                                    title={
                                      conversation.secondsTranscribed === 0
                                        ? 'Start recording your visit'
                                        : "Don't forget to Comprehend the visit"
                                    }
                                  />
                                ) : null;
                              })()}
                              {conversation.conversationName ||
                                'Untitled Visit'}
                            </div>
                            <div
                              className="text-muted-bold"
                              style={{
                                fontSize: '0.8rem',
                                color: selectedConversationIds.includes(
                                  conversation.conversationId,
                                )
                                  ? 'rgba(255,255,255,0.8)'
                                  : undefined,
                              }}
                            >
                              {conversation.patientName || 'Patient'}
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      {showReview && (
        <UnplacedReview
          apiClient={apiClient}
          onAttributionHints={(hints) => recordAttributionHints(sessionEmail, hints)}
          unplaced={pendingLines}
          placed={review.placed}
          describeVisit={describeVisit}
          onChooseUnplaced={(lineId, choice) =>
            chooseVisit(sessionEmail, lineId, choice)
          }
          onMovePlaced={(lineId, conversationId) =>
            movePlaced(sessionEmail, lineId, conversationId)
          }
          onRemovePlaced={(lineId, removed) =>
            removePlaced(sessionEmail, lineId, removed)
          }
          onDone={() => finishReview(false)}
          onDismiss={() => finishReview(true)}
          busy={isPlacing}
        />
      )}
      {tooltipData && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: tooltipData.width,
            maxHeight: 260,
            overflowY: 'auto',
            zIndex: 2000,
            background: '#111827',
            color: '#f9fafb',
            padding: '12px',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          }}
          onMouseEnter={() => {
            if (hideTimerRef.current) {
              clearTimeout(hideTimerRef.current);
              hideTimerRef.current = null;
            }
          }}
          onMouseLeave={clearTooltip}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>
            Transcript Preview
          </div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
            {tooltipData.content || 'Loading...'}
          </div>
        </div>
      )}
      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 3000,
            minWidth: '160px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{ padding: '8px 16px', cursor: 'pointer' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = 'transparent')
            }
            onClick={() => {
              navigate(
                `/User/conversations/details#${contextMenu.conversation.conversationId}`,
                { state: { from: 'multi-recorder' } },
              );
              setContextMenu(null);
            }}
          >
            <i
              className="fa fa-external-link mr-2"
              style={{ fontSize: '0.75rem', color: '#8898aa' }}
            />
            View Visit
          </div>
          <div style={{ borderTop: '1px solid #f0f0f0', margin: '2px 0' }} />
          <div
            style={{ padding: '8px 16px', cursor: 'pointer' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = 'transparent')
            }
            onClick={() => {
              setEditDialog({
                type: 'visit',
                conversation: contextMenu.conversation,
                value: contextMenu.conversation.conversationName || '',
              });
              setContextMenu(null);
            }}
          >
            <i
              className="fa fa-pencil mr-2"
              style={{ fontSize: '0.75rem', color: '#8898aa' }}
            />
            Rename Visit
          </div>
          <div
            style={{ padding: '8px 16px', cursor: 'pointer' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = 'transparent')
            }
            onClick={() => {
              const conv = contextMenu.conversation;
              setContextMenu(null);
              const alreadyLoaded = (apiClient.patients || []).some(
                (p) => p.uid === conv.group,
              );
              if (alreadyLoaded) {
                setEditPatientConversation(conv);
              } else {
                apiClient
                  .updatePatients()
                  .finally(() => setEditPatientConversation(conv));
              }
            }}
          >
            <i
              className="fa fa-user mr-2"
              style={{ fontSize: '0.75rem', color: '#8898aa' }}
            />
            Edit Patient Name
          </div>
        </div>
      )}
      {editDialog && (
        <div className="modal-overlay" onClick={handleSaveEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="mb-4">Rename Visit</h2>
            <div className="form-group">
              <Label>Visit Name:</Label>
              <input
                className="form-control"
                value={editDialog.value}
                onChange={(e) =>
                  setEditDialog((prev) => ({ ...prev, value: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveEdit();
                  if (e.key === 'Escape') setEditDialog(null);
                }}
                autoFocus
              />
            </div>
            <div className="modal-buttons">
              <Button color="primary" onClick={handleSaveEdit}>
                Save
              </Button>
              <Button
                color="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditDialog(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      <PatientModal
        isOpen={!!editPatientConversation}
        onClose={() => setEditPatientConversation(null)}
        onSave={handleSavePatient}
        isEditMode={true}
        patient={(apiClient.patients || []).find(
          (p) => p.uid === editPatientConversation?.group,
        )}
      />
      {showDatePicker && (
        <>
          <div
            onClick={() => setShowDatePicker(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 9998,
            }}
          />
          <div
            ref={datePickerRef}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              zIndex: 9999,
            }}
          >
            <DatePicker
              selected={startDate}
              onChange={(dates) => {
                const [start, end] = dates;
                if (start && end) setShowDatePicker(false);
                setStartDate(start);
                setEndDate(end);
                setDateInputValue(
                  `${formatDateDisplay(start)} - ${end ? formatDateDisplay(end) : ''}`,
                );
                saveDateRange(start, end);
              }}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              inline
              dateFormat="MM/dd/yyyy"
            />
          </div>
        </>
      )}
      <ToastContainer />
    </>
  );
};

export default MultiRecorder;
